import { Timestamp } from 'firebase/firestore';
import {
  addInZone, adjustInZone, formatDateInZone, formatTimeInZone, isTimeZone, wallClockOf,
} from 'src/kernel/time';
import { toValue } from 'vue';

/**
 * Zone-aware conversions for the design system's date and time fields, over the shared kernel.
 *
 * The design system owns the *mechanism* of showing a time as it appears in a zone; the application
 * owns the *policy* of which zone that is, and passes it in as a prop. Private to the design system:
 * app code reads zones through `useTime()` or a context's `ui/`, never through this.
 *
 * Unlike the kernel, a null or unknown zone falls back to the host's zone rather than throwing,
 * because the fields document that fallback for an unset `timeZone` prop, and a field must still
 * render while its zone is loading.
 */

/**
 * @param {string|import('vue').Ref<string>|(() => string)} timezone - IANA zone name, or a
 *   reactive source of one. Resolved per conversion, so a computed that formats through these
 *   helpers re-runs when the zone changes.
 */
// eslint-disable-next-line import/prefer-default-export
export function useZonedTime(timezone) {
  const zone = () => {
    const requested = toValue(timezone);

    return isTimeZone(requested) ? requested : Intl.DateTimeFormat().resolvedOptions().timeZone;
  };
  const toInstant = (value) => (value instanceof Timestamp ? value.toDate() : new Date(value));

  /**
   * @param {Timestamp|Date|string} date
   * @param {Object} options - Luxon unit names: year, month (1-based), day, hour, minute, ...
   * @return {Date}
   */
  const adjustDate = (date, options) => adjustInZone(toInstant(date), options, zone());

  /**
   * Like native `.toLocaleTimeString()`, in the zone.
   *
   * @param {Timestamp|Date|string} date
   * @param {{locales?: string, options?: Object}=} opts
   * @return {string}
   */
  const getLocaleTimeString = (date, opts = {}) => formatTimeInZone(toInstant(date), zone(), opts);

  /**
   * Like native `.toLocaleDateString()`, in the zone.
   *
   * @param {Timestamp|Date|string} date
   * @param {{locales?: string, options?: Object}=} opts
   * @return {string}
   */
  const getLocaleDateString = (date, opts = {}) => formatDateInZone(toInstant(date), zone(), opts);

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const getHours = (date) => wallClockOf(toInstant(date), zone()).hour;

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const getMinutes = (date) => wallClockOf(toInstant(date), zone()).minute;

  /**
   * @param {Timestamp|Date|string} date
   * @param {Object} options - Luxon units: days, hours, minutes, ...
   * @return {Date}
   */
  const addToDate = (date, options) => addInZone(toInstant(date), options, zone());

  return {
    addToDate, getMinutes, getHours, adjustDate, getLocaleTimeString, getLocaleDateString,
  };
}
